import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";

import { McpServerRecord, McpTokenRecord } from "@/shared/domain/mcp";
import { ServerFavicon } from "@/shared/domain/mcp/components/ServerFavicon";
import { useMcpOrgContext } from "@/shared/domain/mcp/hooks/useMcpOrgContext";
import { useSetMcpOrgContext } from "@/shared/domain/mcp/hooks/useSetMcpOrgContext";
import { Button } from "@/shared/foundation/primitives";

import styles from "./McpServerCard.module.css";

interface McpServerCardProps {
  server: McpServerRecord;
  orgId: string | undefined;
  token?: McpTokenRecord;
  currentUserId?: string;
  isOrgAdmin: boolean;
  onConnect: (serverId: string) => void;
  onDisconnect: (serverId: string) => void;
  connectLoading?: boolean;
}

export function McpServerCard({
  server,
  orgId,
  token,
  currentUserId,
  isOrgAdmin,
  onConnect,
  onDisconnect,
  connectLoading,
}: McpServerCardProps) {
  const isOwnToken = token !== undefined && token.user_id === currentUserId;
  const isOwnConnected = isOwnToken;
  const isOwnExpired =
    isOwnToken &&
    (token.token_status === "expired" ||
      token.token_status === "refresh_failed");
  const isOwnValid = isOwnConnected && !isOwnExpired;

  const { data: orgContext, isLoading: contextLoading } = useMcpOrgContext(
    server.server_id,
    orgId,
  );
  const setContextMutation = useSetMcpOrgContext(orgId);

  const [editingContext, setEditingContext] = React.useState(false);
  const [contextDraft, setContextDraft] = React.useState("");

  const savedContext = orgContext?.context ?? "";
  const hasContext = savedContext.trim().length > 0;

  const handleEditContext = () => {
    setContextDraft(savedContext);
    setEditingContext(true);
  };

  const handleSaveContext = () => {
    setContextMutation.mutate(
      { serverId: server.server_id, context: contextDraft },
      {
        onSuccess: () => setEditingContext(false),
      },
    );
  };

  const handleCancelEdit = () => {
    setEditingContext(false);
    setContextDraft("");
  };

  return (
    <Card variant="outlined" className={styles.card}>
      <CardContent className={styles.cardContent}>
        <div className={styles.header}>
          <div className={styles.serverName}>
            <ServerFavicon
              issuerUrl={server.oauth_issuer_url}
              displayName={server.display_name}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {server.display_name}
            </Typography>
            {isOwnValid && (
              <Chip label="Connected" color="success" size="small" />
            )}
            {isOwnExpired && (
              <Chip label="Expired" color="warning" size="small" />
            )}
            {!isOwnConnected && <Chip label="Not connected" size="small" />}
          </div>
          <div className={styles.actions}>
            {isOwnConnected ? (
              <>
                {isOwnExpired && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onConnect(server.server_id)}
                    disabled={connectLoading}
                  >
                    Reconnect
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => onDisconnect(server.server_id)}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Tooltip
                title={
                  isOrgAdmin && !hasContext
                    ? "Add AI context before connecting"
                    : ""
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onConnect(server.server_id)}
                    disabled={connectLoading || (isOrgAdmin && !hasContext)}
                  >
                    Connect
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {isOwnExpired && (
          <Alert severity="warning" className={styles.alert}>
            Your authorization has expired. Please reconnect to continue using
            this service.
          </Alert>
        )}

        {isOrgAdmin && (
          <div className={styles.contextSection}>
            <div className={styles.contextHeader}>
              <Typography variant="subtitle2">AI Context</Typography>
              {!editingContext && !contextLoading && (
                <Tooltip title={hasContext ? "Edit context" : "Add context"}>
                  <IconButton size="small" onClick={handleEditContext}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            {contextLoading && <CircularProgress size={16} />}
            {!contextLoading && editingContext && (
              <div className={styles.contextEdit}>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  size="small"
                  value={contextDraft}
                  onChange={(e) => setContextDraft(e.target.value)}
                  placeholder="Tell the AI how your org uses this server (e.g. which repos to target, project conventions, preferred workflows)."
                />
                {setContextMutation.isError && (
                  <Alert severity="error">
                    Failed to save context. Please try again.
                  </Alert>
                )}
                <div className={styles.contextEditActions}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveContext}
                    disabled={
                      setContextMutation.isPending ||
                      contextDraft.trim().length === 0
                    }
                  >
                    {setContextMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            )}
            {!contextLoading && !editingContext && hasContext && (
              <Typography
                variant="body2"
                color="text.secondary"
                className={styles.contextText}
              >
                {savedContext}
              </Typography>
            )}
            {!contextLoading && !editingContext && !hasContext && (
              <Alert severity="warning" className={styles.alert}>
                No AI context set. Add context to tell the AI how your org uses
                this server before connecting.
              </Alert>
            )}
          </div>
        )}

        {!isOrgAdmin && hasContext && (
          <div className={styles.contextSection}>
            <Typography variant="subtitle2">AI Context</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              className={styles.contextText}
            >
              {savedContext}
            </Typography>
          </div>
        )}

        {server.scopes.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Scopes: {server.scopes.join(", ")}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
